import React, { useMemo, useState } from 'react';
import { AlertTriangle, BookOpen, Check, ChevronDown, ChevronRight, EyeOff, Lightbulb, RefreshCw } from 'lucide-react';
import * as service from '../../../services/bauordnungService';
import { EntwurfInput } from './EntwurfInput';
import type { SchreibKontext } from '../../../services/bauordnungService';
import type { ProjektSteckbrief } from '../../../services/projektSteckbrief';
import { DOKUMENTE } from '../kapitel';
import {
    HANDBUCH_FELD_KAPITEL, HANDBUCH_FELD_LABEL, istAusHandbuchUebernommen, uebernahmeAusHandbuch, uebernahmeIstLeer,
    type HandbuchQuelle,
} from '../uebernahmeHandbuch';
import { grundlagenVorschlaege, type GrundlagenVorschlag } from '../vorschlaege';
import type { Bauordnung, BauordnungBundle } from '../types';

/**
 * Kopfdaten: wer erlaesst, wofuer es gilt, was erzeugt wird.
 *
 * Der Erlassende ist der AUFTRAGGEBER — nur er hat das Hausrecht und kann die
 * Verbindlichkeit vertraglich herstellen. Das verfassende Buero erscheint
 * ausschliesslich in der Fusszeile; ein Unterschriftsfeld bekommt es nicht.
 *
 * ═══ ZWEI QUELLEN, BEIDE NUR AUF KLICK ════════════════════════════════════
 * Die Leiste „Aus der Planungsphase" zeigt, was der Projektsteckbrief fuer
 * Erlassenden und Geltung hergibt (`vorschlaege.ts`); der Kasten „Aus dem
 * Handbuch" bietet die EINMALIGE Uebernahme der fuenf Ordnungsfelder an
 * (`uebernahmeHandbuch.ts`). Keiner der beiden Wege schreibt von selbst —
 * jede Zeile hier zeigt an und ruft auf, entschieden wird in den `.ts`-Dateien.
 */

interface Props {
    bundle: BauordnungBundle;
    ctx: SchreibKontext;
    gesperrt: boolean;
    schreiben: <T>(vorgang: () => Promise<T>) => Promise<T | null>;
    /** Projektsteckbrief — `null`, solange er nicht gelesen ist. */
    steckbrief: ProjektSteckbrief | null;
    /** Das SiGe-Handbuch des Projekts; `undefined` = noch nicht gelesen. Lesefehler stehen in `fehler`. */
    handbuch: HandbuchQuelle | undefined;
    /** Liest das Handbuch erneut — der Knopf im Fehlerfall. */
    onHandbuchNeuLaden: () => void;
}

const Feld: React.FC<{
    label: string; hinweis?: string; children: React.ReactNode; breit?: boolean;
}> = ({ label, hinweis, children, breit }) => (
    <div className={breit ? 'sm:col-span-2' : ''}>
        <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">{label}</label>
        <div className="mt-1">{children}</div>
        {hinweis && <p className="text-[11px] text-slate-400 mt-1 leading-snug">{hinweis}</p>}
    </div>
);

const eingabe = 'w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-fuchsia-400 focus:outline-none disabled:bg-slate-50';

const knopf = 'inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-md border '
    + 'disabled:opacity-40 disabled:cursor-not-allowed';

const tagDeutsch = (iso?: string | null): string => {
    const t = String(iso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
    const [j, m, d] = t.split('-');
    return `${d}.${m}.${j}`;
};

/**
 * Die Leiste „Aus der Planungsphase" — Vorschlaege fuer Erlassenden und
 * Geltung. „Ausblenden" gilt nur fuer diese Sitzung; gespeichert wird nichts.
 */
const Vorschlagsleiste: React.FC<{
    vorschlaege: GrundlagenVorschlag[];
    gesperrt: boolean;
    onUebernehmen: (v: GrundlagenVorschlag) => void;
}> = ({ vorschlaege, gesperrt, onUebernehmen }) => {
    const [offen, setOffen] = useState(true);
    const [ausgeblendet, setAusgeblendet] = useState<ReadonlySet<string>>(new Set());

    const sichtbar = vorschlaege.filter(v => !ausgeblendet.has(v.id));
    if (!sichtbar.length) return null;

    return (
        <section className="bg-blue-50 border border-blue-200 rounded-2xl">
            <button
                type="button"
                onClick={() => setOffen(o => !o)}
                className="flex items-center gap-2 w-full text-left px-4 py-2.5"
                aria-expanded={offen}
            >
                {offen ? <ChevronDown className="w-4 h-4 text-blue-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-blue-500 shrink-0" />}
                <Lightbulb className="w-4 h-4 text-blue-600 shrink-0" />
                <span className="text-sm font-bold text-blue-900 truncate">
                    Aus der Planungsphase ({sichtbar.length})
                </span>
            </button>
            {offen && (
                <div className="border-t border-blue-200">
                    <p className="px-4 pt-2.5 text-[11px] text-blue-800 leading-snug">
                        Aus Projektsteckbrief und Planungsphase abgeleitet — nichts davon ist gesetzt. Übernommen
                        wird je Zeile per Klick; „Ausblenden“ gilt nur für diese Sitzung.
                    </p>
                    <ul className="divide-y divide-blue-100">
                        {sichtbar.map(v => (
                            <li key={v.id} className="px-4 py-2.5 flex items-start gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs text-slate-800 leading-snug">
                                        <span className="font-bold">{v.label}</span>
                                        <span className="text-slate-400"> → </span>
                                        <span className="font-bold text-blue-700">{v.wertText}</span>
                                        {v.ersetzt && (
                                            <span className="text-slate-500"> (ersetzt „{v.ersetzt}“)</span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                                        <span className="font-bold">Quelle:</span> {v.quelle}
                                        {v.stand !== 'belegt' && v.grund && (
                                            <span className="text-amber-700"> · {v.grund}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => onUebernehmen(v)}
                                        disabled={gesperrt}
                                        title="In den Kopfsatz übernehmen"
                                        className={`${knopf} text-blue-700 bg-white border-blue-300 hover:bg-blue-100`}
                                    >
                                        <Check className="w-3.5 h-3.5" /> Übernehmen
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setAusgeblendet(s => new Set([...s, v.id]))}
                                        title="Für diese Sitzung ausblenden — nichts wird gespeichert"
                                        className={`${knopf} text-slate-500 bg-white border-slate-200 hover:bg-slate-50`}
                                    >
                                        <EyeOff className="w-3.5 h-3.5" /> Ausblenden
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
};

export const GrundlagenReiter: React.FC<Props> = ({
    bundle, ctx, gesperrt, schreiben, steckbrief, handbuch, onHandbuchNeuLaden,
}) => {
    const o = bundle.ordnung;
    const vorschlaege = useMemo(() => grundlagenVorschlaege(steckbrief, o), [steckbrief, o]);
    const uebernahme = useMemo(() => uebernahmeAusHandbuch(bundle, handbuch?.wert ?? null), [bundle, handbuch]);
    if (!o) return null;

    const setze = (patch: Partial<Bauordnung>) =>
        schreiben(() => service.updateOrdnung(o.id, patch, ctx));

    const zusatzUmschalten = (kennung: string) => {
        const drin = o.hse_zusatzkapitel.includes(kennung);
        setze({
            hse_zusatzkapitel: drin
                ? o.hse_zusatzkapitel.filter(k => k !== kennung)
                : [...o.hse_zusatzkapitel, kennung],
        });
    };

    const waehlbare = DOKUMENTE.hse.kapitel.filter(e => e.waehlbar);
    const uebernommen = istAusHandbuchUebernommen(o);

    return (
        <div className="space-y-6 max-w-3xl">
            {!gesperrt && (
                <Vorschlagsleiste vorschlaege={vorschlaege} gesperrt={gesperrt} onUebernehmen={v => setze(v.patch)} />
            )}

            <section>
                <h3 className="text-sm font-bold text-slate-800 mb-3">Wer erlässt das Dokument?</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Feld label="Rolle des Erlassenden">
                        <select
                            value={o.erlassender_rolle ?? ''}
                            onChange={e => setze({ erlassender_rolle: (e.target.value || null) as Bauordnung['erlassender_rolle'] })}
                            disabled={gesperrt} className={eingabe}
                        >
                            <option value="">— bitte wählen —</option>
                            <option value="bauherr">Bauherr</option>
                            <option value="gu">Generalunternehmer</option>
                            <option value="bauleitung">Bauleitung</option>
                        </select>
                    </Feld>
                    <Feld label="Firma" hinweis="Erscheint als Herausgeber auf dem Deckblatt.">
                        <EntwurfInput wert={o.erlassender_firma} disabled={gesperrt} className={eingabe}
                            aufSpeichern={v => setze({ erlassender_firma: v })} />
                    </Feld>
                    <Feld label="Unterzeichner" hinweis="Trägt das Unterschriftsfeld — wer nicht erlässt, unterschreibt auch nicht.">
                        <EntwurfInput wert={o.erlassender_name} disabled={gesperrt} className={eingabe}
                            aufSpeichern={v => setze({ erlassender_name: v })} />
                    </Feld>
                    <Feld label="Funktion">
                        <EntwurfInput wert={o.erlassender_funktion} disabled={gesperrt} className={eingabe}
                            placeholder="z. B. Bauleitung"
                            aufSpeichern={v => setze({ erlassender_funktion: v })} />
                    </Feld>
                    <Feld
                        label="Organisationsmodell"
                        hinweis="Vergabeform des Auftragnehmers. Die Arbeitsgemeinschaft kennt nur dieses Modul — das Handbuch führt sie nicht."
                    >
                        <select
                            value={o.org_modell ?? ''}
                            onChange={e => setze({ org_modell: (e.target.value || null) as Bauordnung['org_modell'] })}
                            disabled={gesperrt} className={eingabe}
                        >
                            <option value="">— noch nicht bestimmt —</option>
                            <option value="einzelvergabe">Einzelvergabe</option>
                            <option value="gu">Generalunternehmer (GU)</option>
                            <option value="gue">Generalübernehmer (GÜ)</option>
                            <option value="arge">Arbeitsgemeinschaft (ARGE)</option>
                            <option value="tu">Totalunternehmer (TU)</option>
                        </select>
                    </Feld>
                    <Feld breit label="Verfasserzeile" hinweis="Steht klein in der Fußzeile jeder Seite, nie im Kopf.">
                        <EntwurfInput wert={o.verfasser_zeile} disabled={gesperrt} className={eingabe}
                            placeholder="Verfasst im Auftrag des Bauherrn durch …"
                            aufSpeichern={v => setze({ verfasser_zeile: v })} />
                    </Feld>
                </div>
            </section>

            <section>
                <h3 className="text-sm font-bold text-slate-800 mb-3">Geltung</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Feld breit label="Geltungsbereich">
                        <EntwurfInput wert={o.geltung_ort} disabled={gesperrt} className={eingabe}
                            placeholder="Baustelle, Bauabschnitt oder Gelände"
                            aufSpeichern={v => setze({ geltung_ort: v })} />
                    </Feld>
                    <Feld label="Gültig von">
                        <input type="date" value={o.geltung_von ?? ''} disabled={gesperrt} className={eingabe}
                            onChange={e => setze({ geltung_von: e.target.value || null })} />
                    </Feld>
                    <Feld label="Gültig bis">
                        <input type="date" value={o.geltung_bis ?? ''} disabled={gesperrt} className={eingabe}
                            onChange={e => setze({ geltung_bis: e.target.value || null })} />
                    </Feld>
                    <Feld label="Stand">
                        <input type="date" value={o.stand ?? ''} disabled={gesperrt} className={eingabe}
                            onChange={e => setze({ stand: e.target.value || null })} />
                    </Feld>
                    <Feld label="Fassung">
                        <EntwurfInput wert={o.version_label ?? ''} disabled={gesperrt} className={eingabe}
                            placeholder="z. B. Rev. 01"
                            aufSpeichern={v => setze({ version_label: v || null })} />
                    </Feld>
                </div>
            </section>

            {/* ─── Einmalige Uebernahme aus dem Handbuch ────────────────────── */}
            <section className="bg-white border border-slate-200 rounded-2xl p-4">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-fuchsia-600" /> Aus dem SiGe-Handbuch
                </h3>
                {uebernommen ? (
                    <p className="text-[12px] text-slate-600 mt-1.5 leading-relaxed">
                        Übernommen am {tagDeutsch(o.uebernommen_handbuch_am)}. Seitdem werden Handbuch und
                        Baustellenordnung getrennt geführt — eine zweite Übernahme gibt es nicht.
                    </p>
                ) : handbuch === undefined ? (
                    <p className="text-[12px] text-slate-400 mt-1.5">Das Handbuch wird gelesen…</p>
                ) : handbuch.fehler ? (
                    /* Ein Lesefehler ist KEIN „kein Handbuch": die Uebernahme bleibt
                       gesperrt, bis das Lesen gelingt — sonst gaelte „nichts zu
                       uebernehmen", obwohl etwas da sein kann. */
                    <div className="mt-1.5 bg-amber-50 border border-amber-200 rounded-xl p-3">
                        <p className="text-[12px] text-amber-900 flex items-start gap-1.5 leading-relaxed">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            <span>
                                Das SiGe-Handbuch konnte nicht gelesen werden: {handbuch.fehler} Ob es eines gibt,
                                ist damit offen — die Übernahme ist gesperrt, bis das Lesen gelingt.
                            </span>
                        </p>
                        <button
                            type="button"
                            onClick={onHandbuchNeuLaden}
                            className="mt-2 text-[12px] font-bold text-amber-900 border border-amber-300 bg-white rounded-lg px-3 py-1.5 flex items-center gap-1.5 hover:bg-amber-100"
                        >
                            <RefreshCw className="w-3.5 h-3.5" /> Erneut laden
                        </button>
                    </div>
                ) : handbuch.wert === null ? (
                    <p className="text-[12px] text-slate-500 mt-1.5 leading-relaxed">
                        Zu diesem Projekt gibt es kein SiGe-Handbuch — nichts zu übernehmen.
                    </p>
                ) : uebernahmeIstLeer(uebernahme) ? (
                    <p className="text-[12px] text-slate-500 mt-1.5 leading-relaxed">
                        Das Handbuch führt noch keine Baustellenordnung (Arbeitszeiten, Zugang, Regeln,
                        Notfallschritte, Sammelplatz) — nichts zu übernehmen.
                    </p>
                ) : uebernahme && (
                    <>
                        <p className="text-[12px] text-slate-500 mt-1.5 leading-relaxed">
                            Einmalige Übernahme der Startwerte. Jede Zeile kommt als <strong>unbestätigte</strong> Regel
                            mit Herkunft „Handbuch“ in ihr Kapitel und wird dort einzeln bestätigt — erst dann steht
                            sie im Dokument. Danach werden beide Dokumente getrennt geführt.
                        </p>
                        <ul className="mt-2 space-y-0.5 text-[12px] text-slate-700">
                            {uebernahme.jeFeld.map(f => (
                                <li key={f.feld}>
                                    <span className="font-semibold">{HANDBUCH_FELD_LABEL[f.feld]}</span>
                                    {' '}— {f.anzahl} {f.anzahl === 1 ? 'Zeile' : 'Zeilen'} → Kapitel „{HANDBUCH_FELD_KAPITEL[f.feld]}“
                                </li>
                            ))}
                            {uebernahme.org_modell && (
                                <li>
                                    <span className="font-semibold">Organisationsmodell</span> — {uebernahme.org_modell}
                                </li>
                            )}
                            {uebernahme.uebersprungen.length > 0 && (
                                <li className="text-slate-400">
                                    {uebernahme.uebersprungen.length} {uebernahme.uebersprungen.length === 1 ? 'Zeile steht' : 'Zeilen stehen'} schon
                                    im Zielkapitel und {uebernahme.uebersprungen.length === 1 ? 'wird' : 'werden'} nicht doppelt angelegt.
                                </li>
                            )}
                        </ul>
                        <button
                            type="button"
                            onClick={() => schreiben(() => service.uebernehmeAusHandbuch(o, uebernahme, ctx))}
                            disabled={gesperrt || ctx.nurAnsicht}
                            className="mt-3 bg-fuchsia-600 text-white text-sm font-bold rounded-lg px-4 py-2 flex items-center gap-1.5 hover:bg-fuchsia-700 disabled:bg-slate-200 disabled:text-slate-400"
                        >
                            <Check className="w-4 h-4" /> Einmalig aus dem Handbuch übernehmen
                        </button>
                    </>
                )}
            </section>

            <section>
                <h3 className="text-sm font-bold text-slate-800 mb-3">Welche Dokumente werden erzeugt?</h3>
                <div className="space-y-2">
                    <label className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-3 cursor-pointer">
                        <input type="checkbox" checked={o.erzeugt_ordnung} disabled={gesperrt} className="mt-1"
                            onChange={e => setze({ erzeugt_ordnung: e.target.checked })} />
                        <span>
                            <span className="text-sm font-semibold text-slate-800">Baustellenordnung</span>
                            <span className="block text-[12px] text-slate-500">
                                Die Hausordnung der Baustelle — bewusst kurz. Länge ist hier ein Mangel.
                            </span>
                        </span>
                    </label>
                    <label className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-3 cursor-pointer">
                        <input type="checkbox" checked={o.erzeugt_hse} disabled={gesperrt} className="mt-1"
                            onChange={e => setze({ erzeugt_hse: e.target.checked })} />
                        <span>
                            <span className="text-sm font-semibold text-slate-800">HSE-Plan</span>
                            <span className="block text-[12px] text-slate-500">
                                Der ausführliche Managementplan mit Anlagen je Gefährdung und Anhängen.
                            </span>
                        </span>
                    </label>
                </div>
            </section>

            {o.erzeugt_hse && (
                <section>
                    <h3 className="text-sm font-bold text-slate-800 mb-1">Wählbare Zusatzkapitel des HSE-Plans</h3>
                    <p className="text-[12px] text-slate-500 mb-3">
                        Nicht vorbelegt: ein Kapitel, das niemand ausgewählt hat, würde sonst als geregelt gelten.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {waehlbare.map(e => {
                            const drin = o.hse_zusatzkapitel.includes(e.kapitel);
                            return (
                                <button
                                    key={e.kapitel}
                                    onClick={() => zusatzUmschalten(e.kapitel)}
                                    disabled={gesperrt}
                                    className={`text-[12px] font-semibold rounded-lg px-2.5 py-1.5 border transition-colors disabled:opacity-60 ${
                                        drin
                                            ? 'bg-fuchsia-50 border-fuchsia-300 text-fuchsia-700'
                                            : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                                    }`}
                                >
                                    {e.kapitel}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
};
